import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, ListGroup, Modal, Tab, Tabs } from 'react-bootstrap';
import axios from 'axios';
import WorkoutForm from './WorkoutForm.js';
import './Routines.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://iron.nextriking.com/api';
const API_URL = `${API_BASE_URL}/routines`;

const formatSeries = (value) => {
  try {
    const series = Array.isArray(value) ? value : JSON.parse(value || '[]');
    return series.length
      ? series.map(({ series: sets, peso, repeticiones }) => `${sets} x ${repeticiones} (${peso} kg)`).join(', ')
      : 'Sin series';
  } catch {
    return 'Sin series';
  }
};

const EditableSelect = ({
  label,
  category,
  controlId,
  options,
  selectedOption,
  onSelect,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [newOption, setNewOption] = useState('');
  const [editedOptions, setEditedOptions] = useState({});

  useEffect(() => {
    setEditedOptions(Object.fromEntries(options.map((option) => [option.id, option.name])));
  }, [options]);

  const addOption = async () => {
    const name = newOption.trim();
    if (!name) return;

    await onAdd(name, category);
    setNewOption('');
  };

  const editOption = async (option) => {
    const name = (editedOptions[option.id] || '').trim();
    if (!name || name === option.name) return;

    await onEdit(option.id, name, category);
  };

  const deleteOption = async (option) => {
    await onDelete(option.id);
  };

  return (
    <Form.Group className="mb-3" controlId={controlId}>
      <Form.Label>{label}</Form.Label>
      <Form.Select
        value={selectedOption}
        onChange={(event) => onSelect(event.target.value)}
        aria-label={`Selecciona un ${label.toLowerCase()}`}
        required
      >
        <option value="">Selecciona...</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </Form.Select>

      <Button type="button" variant="outline-secondary" className="mt-2" onClick={() => setShowModal(true)}>
        Administrar opciones de {label.toLowerCase()}
      </Button>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Administrar {label.toLowerCase()}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-4" controlId={`${controlId}-new-option`}>
            <Form.Label>Agregar {label.toLowerCase()}</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder={`Nombre del ${label.toLowerCase()}`}
                value={newOption}
                onChange={(event) => setNewOption(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addOption();
                  }
                }}
              />
              <Button type="button" variant="primary" onClick={addOption}>Agregar</Button>
            </div>
          </Form.Group>

          <h6>Opciones existentes</h6>
          {options.length ? options.map((option) => (
            <div className="d-flex gap-2 mb-2" key={`edit-${option.id}`}>
              <Form.Control
                type="text"
                value={editedOptions[option.id] ?? option.name}
                aria-label={`Editar ${option.name}`}
                onChange={(event) => setEditedOptions((current) => ({
                  ...current,
                  [option.id]: event.target.value,
                }))}
              />
              <Button type="button" variant="outline-primary" onClick={() => editOption(option)}>Guardar</Button>
              <Button type="button" variant="outline-danger" onClick={() => deleteOption(option)}>Borrar</Button>
            </div>
          )) : <p className="text-muted mb-0">No hay opciones cargadas.</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </Form.Group>
  );
};

const Routines = () => {
  const [users, setUsers] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState('create');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedMuscleOption, setSelectedMuscleOption] = useState('');
  const [selectedMovementOption, setSelectedMovementOption] = useState('');
  const [selectedEquipmentOption, setSelectedEquipmentOption] = useState('');
  const [formFields, setFormFields] = useState([{ series: '', peso: '', repeticiones: '' }]);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [draftExercises, setDraftExercises] = useState([]);
  const [assignedTemplates, setAssignedTemplates] = useState([]);
  const [hasHealthAlert, setHasHealthAlert] = useState(false);
  const [success, setSuccess] = useState(null);

  const fetchData = async () => {
    try {
      const [usersResponse, routinesResponse, templatesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/users`),
        axios.get(API_URL),
        axios.get(`${API_URL}/templates`),
      ]);
      setUsers(Array.isArray(usersResponse.data.body) ? usersResponse.data.body : []);
      setRoutines(Array.isArray(routinesResponse.data.body) ? routinesResponse.data.body : []);
      setTemplates(Array.isArray(templatesResponse.data.body) ? templatesResponse.data.body : []);
    } catch (fetchError) {
      setError('No se pudieron cargar los alumnos o las opciones de rutinas.');
      console.error('Error al cargar datos de rutinas:', fetchError);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedUser) {
      setAssignedTemplates([]);
      setHasHealthAlert(false);
      return;
    }

    let cancelled = false;
    setHasHealthAlert(false);
    Promise.all([
      axios.get(`${API_URL}/assigned/user/${selectedUser}`),
      axios.get(`${API_BASE_URL}/health-records/user/${selectedUser}/alert`),
    ])
      .then(([routinesResponse, healthRecordResponse]) => {
        if (cancelled) return;
        setAssignedTemplates(Array.isArray(routinesResponse.data.body) ? routinesResponse.data.body : []);
        setHasHealthAlert(Boolean(healthRecordResponse.data.body?.hasHealthAlert));
      })
      .catch((fetchError) => {
        if (cancelled) return;
        setError('No se pudieron cargar las rutinas o la ficha médica del alumno.');
        console.error('Error al cargar la información del alumno:', fetchError);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedUser]);

  const saveRoutine = async (data) => {
    try {
      await axios.post(`${API_URL}/add`, data);
      await fetchData();
      setError(null);
    } catch (saveError) {
      setError('No se pudo guardar la opción.');
      console.error('Error al guardar rutina:', saveError);
    }
  };

  const addRoutine = (name, category) => saveRoutine({ id: 0, name, category });

  const editRoutine = (id, name, category) => saveRoutine({ id, name, category });

  const deleteRoutine = async (id) => {
    try {
      await axios.delete(`${API_URL}/delete`, { data: { id } });
      if (selectedMuscleOption === String(id)) setSelectedMuscleOption('');
      if (selectedMovementOption === String(id)) setSelectedMovementOption('');
      if (selectedEquipmentOption === String(id)) setSelectedEquipmentOption('');
      await fetchData();
      setError(null);
    } catch (deleteError) {
      setError('No se pudo borrar la opción.');
      console.error('Error al borrar rutina:', deleteError);
    }
  };

  const optionsByCategory = (category) => routines.filter(
    (routine) => Number(routine.category) === category
  );

  const addExerciseToTemplate = () => {
    if (!selectedMovementOption || !selectedMuscleOption || !selectedEquipmentOption) {
      setError('Selecciona ejercicio, músculo y equipamiento antes de agregarlo.');
      return;
    }
    if (formFields.some((field) => !field.series || !field.peso || !field.repeticiones)) {
      setError('Completa las series, el peso y las repeticiones antes de agregar el ejercicio.');
      return;
    }

    const exercise = {
      exerciseId: selectedMovementOption,
      muscleId: selectedMuscleOption,
      equipmentId: selectedEquipmentOption,
      series: formFields.map((field) => ({
        series: Number(field.series),
        peso: Number(field.peso),
        repeticiones: Number(field.repeticiones),
      })),
    };
    if (youtubeUrl.trim()) exercise.youtubeUrl = youtubeUrl.trim();
    setDraftExercises((current) => [...current, exercise]);
    setSelectedMuscleOption('');
    setSelectedMovementOption('');
    setSelectedEquipmentOption('');
    setFormFields([{ series: '', peso: '', repeticiones: '' }]);
    setYoutubeUrl('');
    setError(null);
  };

  const createTemplate = async (event) => {
    event.preventDefault();
    if (!title.trim() || !category.trim() || !draftExercises.length) {
      setError('Completa el título, la categoría y agrega al menos un ejercicio.');
      return;
    }

    try {
      const templateData = {
        title: title.trim(),
        category: category.trim(),
        exercises: draftExercises,
      };
      const isEditing = Boolean(editingTemplate);
      if (isEditing) await axios.put(`${API_URL}/templates/${editingTemplate.id}`, templateData);
      else await axios.post(`${API_URL}/templates`, templateData);
      setTitle('');
      setCategory('');
      setDraftExercises([]);
      setEditingTemplate(null);
      await fetchData();
      setError(null);
      setSuccess(isEditing ? 'Plantilla actualizada correctamente.' : 'Plantilla creada correctamente.');
    } catch (saveError) {
      setSuccess(null);
      setError(editingTemplate ? 'No se pudo actualizar la plantilla.' : 'No se pudo crear la plantilla.');
      console.error('Error al guardar plantilla:', saveError);
    }
  };

  const assignTemplate = async (event) => {
    event.preventDefault();
    try {
      await axios.post(`${API_URL}/templates/${selectedTemplate}/assignments`, { userId: selectedUser });
      const response = await axios.get(`${API_URL}/assigned/user/${selectedUser}`);
      setAssignedTemplates(Array.isArray(response.data.body) ? response.data.body : []);
      setSelectedTemplate('');
      setError(null);
      setSuccess('Rutina asignada correctamente.');
    } catch (assignError) {
      setSuccess(null);
      setError('No se pudo asignar la rutina.');
      console.error('Error al asignar rutina:', assignError);
    }
  };

  const removeDraftExercise = (index) => setDraftExercises((current) => current.filter((_, exerciseIndex) => exerciseIndex !== index));

  const editTemplate = (template) => {
    setEditingTemplate(template);
    setTitle(template.title);
    setCategory(template.category);
    setDraftExercises(template.exercises.map((exercise) => ({
      exerciseId: String(exercise.exercise.id),
      muscleId: String(exercise.muscle.id),
      equipmentId: String(exercise.equipment.id),
      series: exercise.series,
    })));
    setActiveTab('create');
    setError(null);
    setSuccess(null);
  };

  const cancelTemplateEditing = () => {
    setEditingTemplate(null);
    setTitle('');
    setCategory('');
    setDraftExercises([]);
  };

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API_URL}/templates/${templateId}`);
      await fetchData();
      setError(null);
    } catch (deleteError) {
      setError('No se pudo borrar la plantilla.');
      console.error('Error al borrar plantilla:', deleteError);
    }
  };

  const deleteAssignment = async (assignmentId) => {
    try {
      await axios.delete(`${API_URL}/assignments/${assignmentId}`);
      setAssignedTemplates((current) => current.filter((assignment) => assignment.id !== assignmentId));
      setError(null);
    } catch (deleteError) {
      setError('No se pudo quitar la rutina del alumno.');
      console.error('Error al quitar rutina:', deleteError);
    }
  };

  return (
    <main className="routines-page">
      <Card className="routines-card">
        <Card.Body>
          <header>
            <p className="routines-eyebrow">Planificación de entrenamiento</p>
            <h1 className="routines-heading">Rutinas</h1>
            <p className="routines-description">Creá plantillas, asignálas a tus alumnos y consultá las ya guardadas.</p>
          </header>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key || 'create')} className="routines-tabs mb-4" justify>
            <Tab eventKey="create" title="Crear plantilla">
              <Form onSubmit={createTemplate} noValidate>
                {editingTemplate && <Alert variant="info" className="d-flex justify-content-between align-items-center">Editando: <strong>{editingTemplate.title}</strong><Button type="button" variant="outline-secondary" size="sm" onClick={cancelTemplateEditing}>Cancelar edición</Button></Alert>}
                <div className="builder-steps" aria-label="Pasos para crear una rutina">
                  <div className="builder-step"><strong>1. Datos</strong>Nombrá la plantilla.</div>
                  <div className="builder-step"><strong>2. Ejercicios</strong>Configurá cada bloque.</div>
                  <div className="builder-step"><strong>3. Resumen</strong>Revisá y guardá.</div>
                </div>
                <section className="builder-section">
                  <h2 className="builder-section__heading">Datos de la rutina</h2>
                  <p className="builder-section__description">Identificá la plantilla para encontrarla rápidamente.</p>
                  <div className="routine-fields">
                    <Form.Group controlId="routineTitle"><Form.Label>Título de rutina</Form.Label><Form.Control type="text" placeholder="Ej. Rutina de piernas" value={title} onChange={(event) => setTitle(event.target.value)} required /></Form.Group>
                    <Form.Group controlId="routineCategory"><Form.Label>Categoría</Form.Label><Form.Control type="text" placeholder="Ej. Piernas, empujes o tracciones" value={category} onChange={(event) => setCategory(event.target.value)} required /></Form.Group>
                  </div>
                </section>
                <section className="builder-section">
                  <h2 className="builder-section__heading">Configurá el ejercicio</h2>
                  <p className="builder-section__description">Seleccioná las opciones y definí las series antes de sumarlo a la rutina.</p>
                  <div className="catalog-fields">
                    <EditableSelect label="Movimiento / ejercicio" category={1} controlId="formBasicMovement" options={optionsByCategory(1)} selectedOption={selectedMovementOption} onSelect={setSelectedMovementOption} onAdd={addRoutine} onEdit={editRoutine} onDelete={deleteRoutine} />
                    <EditableSelect label="Músculo" category={2} controlId="formBasicMuscle" options={optionsByCategory(2)} selectedOption={selectedMuscleOption} onSelect={setSelectedMuscleOption} onAdd={addRoutine} onEdit={editRoutine} onDelete={deleteRoutine} />
                    <EditableSelect label="Equipamiento" category={3} controlId="formBasicEquipment" options={optionsByCategory(3)} selectedOption={selectedEquipmentOption} onSelect={setSelectedEquipmentOption} onAdd={addRoutine} onEdit={editRoutine} onDelete={deleteRoutine} />
                  </div>
                  <Form.Group className="mt-3" controlId="youtubeUrl"><Form.Label>Enlace de YouTube <span className="text-muted fw-normal">(opcional)</span></Form.Label><Form.Control type="url" placeholder="https://youtube.com/..." value={youtubeUrl} onChange={(event) => setYoutubeUrl(event.target.value)} /></Form.Group>
                  <div className="mt-3"><WorkoutForm formFields={formFields} onChange={setFormFields} /></div>
                  <Button variant="outline-primary" type="button" className="w-100 mt-3" onClick={addExerciseToTemplate}>Agregar ejercicio a la rutina</Button>
                </section>
                <section className="builder-section">
                  <h2 className="builder-section__heading">Resumen de la plantilla</h2>
                  <p className="builder-section__description">{draftExercises.length} {draftExercises.length === 1 ? 'ejercicio agregado.' : 'ejercicios agregados.'}</p>
                  {draftExercises.map((exercise, index) => {
                        const movement = routines.find((option) => String(option.id) === exercise.exerciseId);
                        const muscle = routines.find((option) => String(option.id) === exercise.muscleId);
                        const equipment = routines.find((option) => String(option.id) === exercise.equipmentId);
                        return (
                          <div className="routine-draft-item" key={`${exercise.exerciseId}-${index}`}>
                            <div><strong>{movement?.name}</strong><div className="text-muted small">{muscle?.name} · {equipment?.name} · {formatSeries(exercise.series)}</div></div>
                            <Button type="button" variant="outline-danger" size="sm" onClick={() => removeDraftExercise(index)}>Quitar</Button>
                          </div>
                        );
                  })}
                  {!draftExercises.length && <p className="routine-summary-empty">Todavía no agregaste ejercicios. Configurá el primero en el paso 2.</p>}
                </section>
                <Button variant="primary" type="submit" className="w-100">{editingTemplate ? 'Guardar cambios' : 'Guardar plantilla'}</Button>
              </Form>
            </Tab>

            <Tab eventKey="assign" title="Asignar a alumno">
              <Form onSubmit={assignTemplate}>
                <Form.Group className="mb-3" controlId="formBasicUser">
                  <Form.Label>Alumno</Form.Label>
                  <Form.Select value={selectedUser} onChange={(event) => setSelectedUser(event.target.value)} required>
                    <option value="">Selecciona un alumno...</option>
                    {users.map((user) => {
                      const fullName = [user.name, user.lastname].filter(Boolean).join(' ');
                      return <option key={user.id} value={user.id}>{fullName || `Alumno ${user.id}`}</option>;
                    })}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3" controlId="templateAssignment">
                  <Form.Label>Plantilla</Form.Label>
                  <Form.Select value={selectedTemplate} onChange={(event) => setSelectedTemplate(event.target.value)} required>
                    <option value="">Selecciona una plantilla...</option>
                    {templates.map((template) => <option key={template.id} value={template.id}>{template.title} ({template.category})</option>)}
                  </Form.Select>
                </Form.Group>
                {hasHealthAlert && <Alert variant="warning" role="alert">Este alumno tiene información médica registrada. Consultá con tu entrenador antes de asignar o modificar una rutina.</Alert>}
                <Button variant="success" type="submit" className="w-100">Asignar plantilla</Button>
              </Form>

              {selectedUser && (
                <div className="mt-4">
                  <h5 className="mb-3">Rutinas asignadas</h5>
                  {assignedTemplates.length ? assignedTemplates.map((assignment) => (
                    <Card className="mb-3" key={assignment.id}>
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                          <div><Card.Title as="h6" className="mb-1">{assignment.routine.title}</Card.Title><Badge bg="secondary">{assignment.routine.category}</Badge></div>
                          <Button type="button" variant="outline-danger" size="sm" onClick={() => deleteAssignment(assignment.id)}>Quitar</Button>
                        </div>
                        <ListGroup variant="flush">
                          {assignment.routine.exercises.map((exercise) => <ListGroup.Item className="px-0" key={exercise.id}><strong>{exercise.exercise.name}</strong><span className="text-muted"> · {exercise.muscle.name} · {exercise.equipment.name} · {formatSeries(exercise.series)}</span></ListGroup.Item>)}
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  )) : <p className="text-muted mb-0">Este alumno no tiene rutinas asignadas.</p>}
                </div>
              )}
            </Tab>

            <Tab eventKey="saved" title={`Plantillas guardadas (${templates.length})`}>
              {templates.length ? <ListGroup>
                {templates.map((template) => (
                  <ListGroup.Item className="d-flex justify-content-between align-items-center gap-3" key={template.id}>
                    <div><strong>{template.title}</strong><div className="text-muted small">{template.category} · {template.exercises.length} ejercicio(s)</div></div>
                    <div className="d-flex gap-2 flex-shrink-0">
                      <Button type="button" variant="outline-secondary" size="sm" onClick={() => setTemplateDetails(template)}>Ver</Button>
                      <Button type="button" variant="outline-primary" size="sm" onClick={() => editTemplate(template)}>Editar</Button>
                      <Button type="button" variant="outline-danger" size="sm" onClick={() => deleteTemplate(template.id)}>Borrar</Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup> : <p className="text-muted mb-0">Todavía no hay plantillas guardadas.</p>}
            </Tab>
          </Tabs>

          <Modal show={Boolean(templateDetails)} onHide={() => setTemplateDetails(null)} centered>
            <Modal.Header closeButton><Modal.Title>{templateDetails?.title}</Modal.Title></Modal.Header>
            <Modal.Body>
              <Badge bg="secondary" className="mb-3">{templateDetails?.category}</Badge>
              <h6>Ejercicios</h6>
              <ListGroup variant="flush">
                {templateDetails?.exercises.map((exercise) => (
                  <ListGroup.Item className="px-0" key={exercise.id}>
                    <strong>{exercise.exercise.name}</strong>
                    <div className="text-muted small">{exercise.muscle.name} · {exercise.equipment.name}</div>
                    <div className="small mt-1">{formatSeries(exercise.series)}</div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Modal.Body>
            <Modal.Footer><Button type="button" variant="secondary" onClick={() => setTemplateDetails(null)}>Cerrar</Button></Modal.Footer>
          </Modal>
        </Card.Body>
      </Card>
    </main>
  );
};

export default Routines;
